import { TitleCasePipe } from "@angular/common";
import {
  AfterViewInit,
  Component,
  effect,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { MatDialog } from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { RouterLink } from "@angular/router";
import { MsalService } from "@azure/msal-angular";
import { FuseMediaWatcherService } from "@fuse/services/media-watcher";
import { BookingService } from "app/modules/booking-page/booking-page.service";
import { GeneralService } from "app/services/general.service";
import { HomeService } from "app/services/home.service";
import { ThemeService, type Scheme } from "app/services/theme.service";
import { UserService } from "app/services/user.service";
import {
  isNotificationEnabled,
  isSsoEnabled,
} from "environments/environment.helper";
import { Subject, takeUntil } from "rxjs";
import { ProfileQrCodeComponentMobile } from "../profile-qr-code/profile-qr-code-mobile.component";
import { ProfileQrCodeComponent } from "../profile-qr-code/profile-qr-code.component";

@Component({
  selector: "user",
  templateUrl: "./user.component.html",
  imports: [
    MatMenuModule,
    MatIconModule,
    MatDividerModule,
    RouterLink,
    TitleCasePipe,
  ],
  standalone: true,
})
export class UserComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() showAvatar: boolean = false;
  @Input() variant: "header" | "sidebar-footer" = "header";

  isSsoEnabled = isSsoEnabled;
  isNotificationEnabled = isNotificationEnabled;

  workingStatus;

  isScreenSmall: boolean;

  user = this._userService.user;
  profilePhoto = this._userService.profilePhoto;
  loginDisplay = this._userService.isLoginDisplay;

  LTRoomList = this._bookingService.LTRoomList;

  currentBrowserNotification;
  appVersion: string = "";

  private readonly _themeService = new ThemeService();

  private readonly _unsubscribeAll: Subject<any> = new Subject<any>();
  constructor(
    private readonly _userService: UserService,
    private readonly _homeService: HomeService,
    private readonly _bookingService: BookingService,
    private readonly _msalService: MsalService,
    private readonly _fuseMediaWatcherService: FuseMediaWatcherService,
    private readonly _matDialog: MatDialog,
    private readonly _matBottomSheet: MatBottomSheet,
    private readonly _generalService: GeneralService
  ) {
    effect(() => {
      const user = this.user();

      const ltroomList = this.LTRoomList();
      if (user && ltroomList) {
        this.workingStatus = ltroomList.find(
          (x: any) => x.deskName === user.displayName
        );
      }
    });
  }

  ngAfterViewInit(): void {
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes("md");
      });
  }

  ngOnInit() {
    this.currentBrowserNotification =
      Notification.permission === "denied" || !("Notification" in window);

    // Get app version
    this._generalService
      .getAppVersion()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((version) => {
        this.appVersion = version;
      });
  }

  setWorkingStatus(status: string) {
    const date = new Date().toDateString().slice(4);
    const payload = {
      displayName: this.user()?.displayName,
      date: date,
      availabilityStatus: status,
    };

    this._homeService.postTLRoomBooking(payload).subscribe();
  }

  openDialog(): void {
    if (this.isScreenSmall && !this.isIntern()) {
      this._matBottomSheet.open(ProfileQrCodeComponentMobile, {
        panelClass: "bottom-sheet-custom-class",
      });
    } else if (!this.isScreenSmall && !this.isIntern()) {
      this._matDialog.open(ProfileQrCodeComponent, {
        panelClass: "banner-css-class",
        backdropClass: "banner-backdrop-class",
        width: "420px",
        maxWidth: "92vw",
      });
    } else {
      return;
    }
  }

  isIntern() {
    return this.user()?.roles.some((role) => role.roleName === "Intern");
  }

  setTheme(theme: Scheme): void {
    this._themeService.setTheme(theme);
  }

  get currentTheme(): Scheme {
    return this._themeService.currentTheme();
  }

  signOut(): void {
    localStorage.removeItem("aemAccessToken");
    localStorage.removeItem("aemUser");

    // Name the account so MSAL drops its cached tokens; without it a stale ID
    // token can survive logout and get replayed at the next sign-in.
    const account = this._msalService.instance.getActiveAccount() ?? undefined;
    this._msalService.logoutRedirect({ account });
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();

    // Cleanup theme service
    this._themeService.destroy();
  }
}
